/**
 * CommandPaletteTrigger — the ⌘K / Ctrl-K quick-jump palette.
 *
 * Renders two things:
 *  1. A pill-shaped "Search…" button that lives in the desktop top-bar next
 *     to the bell + avatar. Click (or press ⌘K / Ctrl-K anywhere in the app)
 *     to open the palette.
 *  2. A shadcn `CommandDialog` populated with the same links parents use
 *     every day: primary nav pages, direct-to-domain insights, activities,
 *     courses and settings shortcuts. Typing filters live.
 *
 * The palette is desktop-first (Ctrl-K is muscle-memory for keyboard users)
 * but nothing here breaks on mobile — the trigger is `hidden md:inline-flex`
 * so it simply doesn't render below md.
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Home, CalendarCheck2, FileText, TrendingUp, GraduationCap,
  MoreHorizontal, Settings, User, Crown, Bell, HelpCircle,
  Sparkles, Search, Activity as ActivityIcon, Clock,
} from "lucide-react";
import {
  CommandDialog, CommandInput, CommandList, CommandEmpty,
  CommandGroup, CommandItem, CommandSeparator, CommandShortcut,
} from "@/components/ui/command";
import { assessment as assessmentApi } from "@/lib/api";
import { getCached } from "@/lib/cache";

// Icon rendered inside a CommandItem — kept tiny so search results stay dense.
const Ico = ({ icon: Icon }) => (
  <Icon className="w-4 h-4 text-inkmid mr-2 shrink-0" strokeWidth={2.2} />
);

const RECENTS_KEY = "kpp.cmd.recents";
const RECENTS_MAX = 5;

/** Read the recents list from localStorage. Guarded because SSR / private
 *  mode / older browsers can throw or return non-array garbage. */
function readRecents() {
  try {
    const raw = window.localStorage.getItem(RECENTS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.slice(0, RECENTS_MAX) : [];
  } catch { return []; }
}

/** Move `entry` to the front of the recents list (max 5, deduped by `id`). */
function pushRecent(entry) {
  try {
    const list = readRecents();
    const next = [entry, ...list.filter((r) => r.id !== entry.id)].slice(0, RECENTS_MAX);
    window.localStorage.setItem(RECENTS_KEY, JSON.stringify(next));
    return next;
  } catch { return null; }
}

export default function CommandPaletteTrigger() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [recents, setRecents] = useState(readRecents);
  const [domains, setDomains] = useState(() => getCached("report:latest")?.domain_scores || []);
  const navigate = useNavigate();

  // ⌘K / Ctrl-K anywhere in the app opens the palette. Also register `/`
  // as a Slack-style shortcut, but only when nothing is focused.
  useEffect(() => {
    function onKey(e) {
      const meta = e.metaKey || e.ctrlKey;
      if (meta && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((v) => !v);
      } else if (e.key === "/" && !isTypingTarget(e.target)) {
        e.preventDefault();
        setOpen(true);
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Lazy-load domain list only when the palette actually opens — keeps the
  // initial dashboard render lean.
  useEffect(() => {
    if (!open || domains.length > 0) return;
    let alive = true;
    assessmentApi.latest()
      .then((r) => alive && r?.domain_scores && setDomains(r.domain_scores))
      .catch(() => {});
    return () => { alive = false; };
  }, [open, domains.length]);

  const primaryNav = useMemo(() => ([
    { id: "home",     label: "Home",     icon: Home,           to: "/home",       hint: "Overview & tips" },
    { id: "today",    label: "Today",    icon: CalendarCheck2, to: "/activities", hint: "Today's activities" },
    { id: "report",   label: "Report",   icon: FileText,       to: "/report",     hint: "Assessment summary" },
    { id: "progress", label: "Progress", icon: TrendingUp,     to: "/progress",   hint: "Track growth" },
    { id: "courses",  label: "Courses",  icon: GraduationCap,  to: "/courses",    hint: "Parent courses" },
    { id: "more",     label: "More",     icon: MoreHorizontal, to: "/more",       hint: "All shortcuts" },
  ]), []);

  const settingsNav = useMemo(() => ([
    { id: "settings-child",   label: "Child Profile",     icon: User,     to: "/child" },
    { id: "settings-account", label: "Account Settings",  icon: Settings, to: "/edit-profile" },
    { id: "settings-plan",    label: "Subscription & Plan", icon: Crown,  to: "/subscription" },
    { id: "settings-notifs",  label: "Notifications",     icon: Bell,     to: "/notifications" },
    { id: "settings-help",    label: "Help & Support",    icon: HelpCircle, to: "/help" },
  ]), []);

  const go = (to, entry) => {
    setOpen(false);
    // Persist the picked entry so it shows up under "Recent" next open.
    if (entry) {
      const next = pushRecent(entry);
      if (next) setRecents(next);
    }
    // Small delay so the dialog closes before we navigate — avoids a jarring
    // "instant swap" flash on the underlying page.
    setTimeout(() => navigate(to), 40);
  };

  // Merge all items into one lookup by id so recents can render icons/labels
  // without duplicating metadata. Domain items are matched on the composite
  // "domain:<key>" id.
  const allItemsById = useMemo(() => {
    const map = {};
    [...primaryNav, ...settingsNav].forEach((it) => { map[it.id] = it; });
    domains.forEach((d) => {
      map[`domain:${d.domain_key}`] = {
        id: `domain:${d.domain_key}`,
        label: d.domain_name,
        icon: ActivityIcon,
        to: `/progress/${encodeURIComponent(d.domain_key)}`,
        hint: `${Math.round(Number(d.percentage || 0))}/100`,
      };
    });
    map["deep-dive"] = { id: "deep-dive", label: "Take a Deep Dive assessment", icon: Sparkles, to: "/deep-dive" };
    return map;
  }, [primaryNav, settingsNav, domains]);

  // Only show recents when the input is empty — once the parent starts
  // typing, the palette's built-in fuzzy filter should own the results.
  const recentItems = query.trim() === ""
    ? recents.map((r) => allItemsById[r.id]).filter(Boolean)
    : [];

  return (
    <>
      {/* Trigger button — desktop-only; mobile users have the drawer + bottom nav. */}
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label="Open quick search"
        data-testid="cmd-palette-trigger"
        className="hidden md:inline-flex items-center gap-2 h-9 pl-3 pr-2 rounded-full border border-plum/12 bg-white/70 hover:bg-white hover:border-plum/20 shadow-soft text-sm text-inkmid transition-colors"
      >
        <Search className="w-4 h-4" strokeWidth={2.2} />
        <span>Search…</span>
        <kbd className="ml-2 hidden lg:inline-block text-[10px] font-bold text-inkmid bg-plum/6 rounded px-1.5 py-0.5 border border-plum/10">
          ⌘K
        </kbd>
      </button>

      <CommandDialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setQuery(""); }}>
        <CommandInput
          value={query}
          onValueChange={setQuery}
          placeholder="Search for a page, insight or activity…"
          data-testid="cmd-palette-input"
        />
        <CommandList data-testid="cmd-palette-list">
          <CommandEmpty>No matches. Try &quot;progress&quot;, &quot;subscription&quot; or a domain name.</CommandEmpty>

          {recentItems.length > 0 && (
            <>
              <CommandGroup heading="Recent">
                {recentItems.map((item) => (
                  <CommandItem
                    key={`recent-${item.id}`}
                    value={`${item.label} recent`}
                    onSelect={() => go(item.to, { id: item.id })}
                    data-testid={`cmd-recent-${item.id}`}
                  >
                    <Ico icon={Clock} />
                    <span className="flex-1">{item.label}</span>
                    {item.hint && <span className="text-[11px] text-inkmid">{item.hint}</span>}
                  </CommandItem>
                ))}
              </CommandGroup>
              <CommandSeparator />
            </>
          )}

          <CommandGroup heading="Jump to">
            {primaryNav.map((item) => (
              <CommandItem
                key={item.id}
                value={`${item.label} ${item.hint}`}
                onSelect={() => go(item.to, { id: item.id })}
                data-testid={`cmd-item-${item.id}`}
              >
                <Ico icon={item.icon} />
                <span className="flex-1">{item.label}</span>
                <span className="text-[11px] text-inkmid">{item.hint}</span>
              </CommandItem>
            ))}
          </CommandGroup>

          {domains.length > 0 && (
            <>
              <CommandSeparator />
              <CommandGroup heading="Domain insights">
                {domains.map((d) => (
                  <CommandItem
                    key={d.domain_key}
                    value={`${d.domain_name} insight domain report`}
                    onSelect={() => go(`/progress/${encodeURIComponent(d.domain_key)}`, { id: `domain:${d.domain_key}` })}
                    data-testid={`cmd-item-domain-${d.domain_key}`}
                  >
                    <Ico icon={ActivityIcon} />
                    <span className="flex-1">{d.domain_name}</span>
                    <span className="text-[11px] font-bold text-inkmid">{Math.round(Number(d.percentage || 0))}/100</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            </>
          )}

          <CommandSeparator />
          <CommandGroup heading="Settings & shortcuts">
            {settingsNav.map((item) => (
              <CommandItem
                key={item.id}
                value={item.label}
                onSelect={() => go(item.to, { id: item.id })}
                data-testid={`cmd-item-${item.id}`}
              >
                <Ico icon={item.icon} />
                <span className="flex-1">{item.label}</span>
              </CommandItem>
            ))}
          </CommandGroup>

          <CommandSeparator />
          <CommandGroup heading="Quick actions">
            <CommandItem
              value="Take deep dive assessment sub-skill"
              onSelect={() => go("/deep-dive", { id: "deep-dive" })}
              data-testid="cmd-item-deep-dive"
            >
              <Ico icon={Sparkles} />
              <span className="flex-1">Take a Deep Dive assessment</span>
              <CommandShortcut>D</CommandShortcut>
            </CommandItem>
          </CommandGroup>
        </CommandList>
      </CommandDialog>
    </>
  );
}

/* Don't hijack "/" when the user is typing in an input, textarea or a
 * contenteditable — otherwise we'd swallow forward-slashes inside forms. */
function isTypingTarget(el) {
  if (!el) return false;
  const tag = (el.tagName || "").toLowerCase();
  return tag === "input" || tag === "textarea" || el.isContentEditable;
}
